/***
 *
 *   SIGN UP STEP 1
 *   Signup form for account owners
 *   Step 1: create account
 *   Step 2: verify email address
 *   Step 3: select plan
 *
 **********/

import React, { useContext, useState, useEffect } from 'react';
// Module imports.
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
// Components import.
import { AuthContext, useNavigate, ViewContext, useLocation } from 'components/lib';
// import { SocialSignin } from 'components/lib';
import codes from 'country-calling-code';
import { MobileSignupForm } from 'components/MobileSignupForm/MobileSignupForm';
import axios from 'axios';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { OtpVerification } from '../signin/otpVerify';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import { InfoOutlined } from '@mui/icons-material';

export function Signup(props) {

    const navigate = useNavigate();
    const location = useLocation()
    const context = useContext(AuthContext);
    const viewContext = useContext(ViewContext)

    const url = window.location.search;
    const signUpParams = new URLSearchParams(url);
    const source = signUpParams.get('source');
    const origin = signUpParams.get('origin');
    const originName = signUpParams.get('name');
    const redirectLink = location?.state?.redirectTo || "/navigate";

    const [showInvitePrompt, setShowInvitePrompt] = useState(false);
    const [inviteLink, setInviteLink] = useState('');
    const [otpInput, setOtpInput] = useState(false);
    const [loading, setLoading] = useState(false);
    const [OTP, setOTP] = useState('');
    const [signUpMethod, setSignupMethod] = useState('phone')
    const [formValues, setFormValues] = useState({
        firstName: '',
        email: '',
        phoneNumber: '',
        countryCode: '91',
        marketing: 1,
        password: '',
        confirmPassword: ''
    });
    const [captcha, setCaptcha] = useState({
        id: "",
        image: "",
        input: ""
    })

    //useEffect
    useEffect(() => {
        if (!otpInput) {
            refreshCaptcha();
        }
    }, [otpInput])


    // Functions 
    const createSignUpEvent = (eventType) => {
        if (source === "website") {
            let event_name;
            if (originName) {
                event_name = `website_${eventType}_${origin}_${originName}`
            } else {
                event_name = `website_${eventType}_${origin}`
            }
            Event(event_name);
            Event(`website_${eventType}`);
        }
    }

    const handleError = (error) => {
        setLoading(false);

        const data = error.response?.data;
        const message = data?.message || data || "Something went wrong";

        if (data?.invite_link) {
            setInviteLink(data.invite_link);
            setShowInvitePrompt(true);
            return;
        }

        if (["CAPTCHA_EXPIRED", "CAPTCHA_INVALID"].includes(data?.errorCode) && data?.captcha) {
            setCaptcha({ id: data.captcha.captchaId, image: data.captcha.image, input: "" });
        }

        if (setOtpInput) {
            setOtpInput(false);
            setOTP('');
        }

        viewContext.notification.show(message, "error", true);
    };

    const validateContact = (value) => {
        if (signUpMethod === 'phone') {
            const mobileNumberRegex = /^\s*(\d\s*){7,15}\s*$/;
            return mobileNumberRegex.test(value);
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
            return emailRegex.test(value);
        }
    }

    const refreshCaptcha = async () => {
        try {
            const res = await axios.get("/api/auth/captcha");
            setCaptcha({
                image: res.data.image,
                id: res.data.captchaId,
                input: "",
            });
        } catch (err) {
            viewContext.notification.show("Failed to load captcha. Please try again later", "error", true);
        }
    }

    //Handling clicks
    const handleSubmit = () => {
        if (loading) return;
        setLoading(true);

        const { phoneNumber, countryCode, email, firstName } = formValues;

        let options;

        if (signUpMethod === 'phone') {
            const validPhone = validateContact(phoneNumber);
            if (!validPhone) {
                viewContext.notification.show(props.t('auth.signin.signinMobileOTP.error.invalidNumber'), 'error');
                setLoading(false);
                return;
            }

            const queryParams = new URLSearchParams({
                sign_up: true,
                mobile: true
            }).toString();

            options = {
                method: 'POST',
                url: `/api/auth/otp?${queryParams}`,
                data: {
                    first_name: firstName,
                    mobile_number: `+${countryCode} ${phoneNumber}`,
                    origin: {
                        source: source || 'portal',
                        origin_type: origin === 'lab' || origin === 'lpath' ? origin : null,
                        origin_name: originName || null
                    },
                    captcha_id: captcha.id,
                    captcha_input: captcha.input
                },
            };
        } else if (signUpMethod === 'email') {
            const validEmail = validateContact(email);
            if (!validEmail) {
                viewContext.notification.show("Invalid email address. Please enter a valid email address", 'error');
                setLoading(false);
                return;
            }

            const queryParams = new URLSearchParams({
                sign_up: true,
                email: true
            }).toString();

            options = {
                method: 'POST',
                url: `/api/auth/otp?${queryParams}`,
                data: {
                    first_name: firstName,
                    email,
                    origin: {
                        source: source || 'portal',
                        origin_type: origin === 'lab' || origin === 'lpath' ? origin : null,
                        origin_name: originName || null
                    },
                    captcha_id: captcha.id,
                    captcha_input: captcha.input
                },
            };
        }

        if (!options) {
            viewContext.notification.show('Invalid signup method.', 'error');
            setLoading(false);
            return;
        }

        axios
            .request(options)
            .then((response) => {
                if ((response.data.otp_sent.email_otp === true) || (response.data.otp_sent.mobile_otp === true)) {
                    setOtpInput(true);
                    viewContext.notification.show(response.data.message, 'success', true);
                } else {
                    viewContext.notification.show(response.data.message, 'error');
                }
            })
            .catch(handleError)
            .finally(() => setLoading(false));
    };

    const verifyClick = () => {
        if (loading) return;
        setLoading(true);

        const isPhoneSignup = signUpMethod === 'phone';

        const options = {
            method: 'POST',
            url: '/api/account/verify',
            data: {
                first_name: formValues.firstName,
                marketing: formValues.marketing,
                ...(isPhoneSignup
                    ? {
                        mobile_otp: OTP,
                        mobile_verification_required: true,
                        mobile_number: formValues.phoneNumber,
                        country_code: formValues.countryCode,
                    }
                    : {
                        email_otp: OTP,
                        email_verification_required: true,
                        email: formValues.email,
                        password: formValues.password,
                    }),
            },
        };

        axios
            .request(options)
            .then(function(response) {
                if (response.data) {
                    window.dataLayer.push({
                        "event": "signup",
                        "data": {
                            "firstName": formValues.firstName,
                            "contact": formValues.email || `${formValues.countryCode} ${formValues.phoneNumber}`
                        }
                    })
                    response.data.redirectLink = redirectLink;
                    (response.data['2fa_required'] ? navigate(`/signin/otp?token=${response.data.token}`) : context.signin(response))
                } else {
                    setLoading(false);
                    setOtpInput(false);
                    viewContext.notification.show(response.data.message, 'error');
                }
            })
            .catch(handleError)
    };

    const handleResend = () => {
        let options;

        if (signUpMethod === 'phone') {
            const validPhone = validateContact(formValues.phoneNumber);
            if (!validPhone) {
                viewContext.notification.show(props.t('auth.signin.signinMobileOTP.error.invalidNumber'), 'error');
                return;
            }

            const queryParams = new URLSearchParams({
                mobile: true
            }).toString();

            options = {
                method: 'POST',
                url: `/api/auth/otp/resend?${queryParams}`,
                data: {
                    first_name: formValues.firstName,
                    mobile_number: `+${formValues.countryCode} ${formValues.phoneNumber}`,
                },
            };
        } else if (signUpMethod === 'email') {
            const validEmail = validateContact(formValues.email);
            if (!validEmail) {
                viewContext.notification.show("Invalid email address. Please enter a valid email address", 'error');
                return;
            }

            const queryParams = new URLSearchParams({
                email: true
            }).toString();

            options = {
                method: 'POST',
                url: `/api/auth/otp/resend?${queryParams}`,
                data: {
                    first_name: formValues.firstName,
                    email: formValues.email,
                },
            };
        }

        if (!options) {
            viewContext.notification.show('Invalid signup method.', 'error');
            return;
        }

        axios
            .request(options)
            .then((response) => {
                if ((response.data.otp_sent.email_otp === true) || (response.data.otp_sent.mobile_otp === true)) {
                    viewContext.notification.show(response.data.message, 'success', true);
                } else {
                    viewContext.notification.show(response.data.message, 'error');
                }
            })
            .catch(handleError)
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCheckboxChange = (e) => {
        setFormValues((prev) => ({
            ...prev,
            marketing: e.target?.checked ? 1 : 0,
        }));
    };

    const handleCaptchaInput = (input) => {
        setCaptcha((prev) => ({ ...prev, input: input }));
    }

    return (
        <>
            {loading ? (
                <LoadingBackdrop isOpen={loading} />
            ) : (
                <>
                    <div id='mainContainer' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                        {!otpInput && (
                            <MobileSignupForm
                                values={formValues}
                                onChange={handleChange}
                                onSubmit={handleSubmit}
                                onCheckboxChange={handleCheckboxChange}
                                captcha={captcha}
                                onCaptchaChange={handleCaptchaInput}
                                onRefreshCaptcha={refreshCaptcha}
                                countryCodes={codes}
                                loading={false}
                                setSignUpMethod={setSignupMethod}
                                signUpMethod={signUpMethod}
                                redirectLink={redirectLink}
                            />

                        )}
                        {otpInput && (
                            <div id='otpVerificationContainer' style={{ display: 'flex', width: '600px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
                                <OtpVerification
                                    OTP={OTP}
                                    setOTP={setOTP}
                                    onEdit={() => { setOtpInput(false); setLoading(false); }}
                                    onVerify={verifyClick}
                                    onResend={handleResend}
                                    onSubmitNewOtp={handleSubmit}
                                    signUpMethod={signUpMethod}
                                    destination={signUpMethod === 'phone' ? formValues.phoneNumber : formValues.email}
                                    t={props.t}
                                    loading={loading}
                                />
                            </div>
                        )}
                    </div>
                    <Dialog
                        open={showInvitePrompt}
                        onClose={() => { }}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle sx={{ color: '#1450a0' }}>
                            <span style={{ display: 'flex', alignItems: 'center' }}>
                                <InfoOutlined sx={{ mr: 1 }} />{props.t('auth.signup.account.backDrop.message.title')}
                            </span>
                        </DialogTitle>

                        <DialogContent>
                            {props.t('auth.signup.account.backDrop.message.info')}
                        </DialogContent>

                        <DialogActions>
                            <AivagamButton
                                type="confirm"
                                buttonText={props.t('auth.signup.account.backDrop.button')}
                                onClick={() => navigate(inviteLink)}
                                classStyles={{
                                    width: '10em',
                                    fontSize: '1em',
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)'
                                }}
                            />
                        </DialogActions>
                    </Dialog>
                </>
            )}
        </>
    );
}
